import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays, ChevronLeft, ChevronRight, ExternalLink,
  Clock, BarChart2, Copy, CheckCircle, AlertTriangle,
} from 'lucide-react';
import { fetchAvailableDates, fetchArticlesByDate } from '../api';

const FETCH_HOURS = new Set([9,10,11,12,13,14,15,16,18,19,20]);
const HAS_HALF = new Set([9,10,11,12,13,14,15,16]);

function formatHour(h) {
  if (h === 0) return '자정';
  if (h < 12) return `오전 ${h}시`;
  if (h === 12) return '정오';
  return `오후 ${h - 12}시`;
}

function SlotPicker({ hour, articles, selectedSlot, onSelectSlot }) {
  if (hour === null) return null;

  const slots = [];
  if (FETCH_HOURS.has(hour)) {
    slots.push({ minuteFrom: 0, minuteTo: 29, label: `${String(hour).padStart(2,'0')}:00` });
    if (HAS_HALF.has(hour))
      slots.push({ minuteFrom: 30, minuteTo: 59, label: `${String(hour).padStart(2,'0')}:30` });
  } else {
    slots.push({ minuteFrom: 0, minuteTo: 59, label: `${String(hour).padStart(2,'0')}:00` });
  }

  return (
    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>수집 회차:</span>
      {slots.map((s) => {
        const cnt = articles.filter((a) => {
          const m = parseInt((a.collected_at || '').slice(14, 16), 10);
          return m >= s.minuteFrom && m <= s.minuteTo;
        }).length;
        const isActive = selectedSlot?.label === s.label;
        return (
          <button key={s.label}
            onClick={() => onSelectSlot(isActive ? null : s)}
            style={{
              padding: '2px 10px', borderRadius: '5px', border: 'none', cursor: 'pointer',
              fontSize: '0.78rem', fontWeight: isActive ? 700 : 400,
              background: isActive ? '#0ea5e9' : '#1e293b',
              color: isActive ? '#fff' : '#94a3b8',
              transition: 'all 0.12s',
            }}
          >
            {s.label} ({cnt}건)
          </button>
        );
      })}
      {selectedSlot && (
        <button onClick={() => onSelectSlot(null)}
          style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.72rem', cursor: 'pointer' }}>
          ✕ 회차 해제
        </button>
      )}
    </div>
  );
}

function HourBar({ hourlyDistribution, selectedHour, onSelectHour }) {
  const maxCount = Math.max(...(hourlyDistribution || []).map((h) => h.count), 1);
  const all = Array.from({ length: 24 }, (_, i) => {
    const found = (hourlyDistribution || []).find((h) => h.hour === i);
    return { hour: i, count: found ? found.count : 0 };
  });

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <BarChart2 size={13} /> 시간대별 수집량 (클릭하면 해당 시간대 필터)
      </div>
      <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '48px' }}>
        {all.map(({ hour, count }) => {
          const pct = count === 0 ? 2 : Math.max((count / maxCount) * 100, 4);
          const isSelected = selectedHour === hour;
          return (
            <div
              key={hour}
              title={`${formatHour(hour)}: ${count}건`}
              onClick={() => onSelectHour(isSelected ? null : hour)}
              style={{
                flex: 1,
                height: `${pct}%`,
                background: isSelected ? '#38bdf8' : count > 0 ? '#334155' : '#1e293b',
                borderRadius: '2px 2px 0 0',
                cursor: count > 0 ? 'pointer' : 'default',
                transition: 'background 0.15s',
                minHeight: '3px',
              }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#475569', marginTop: '2px' }}>
        <span>0시</span><span>6시</span><span>12시</span><span>18시</span><span>23시</span>
      </div>
      {selectedHour !== null && (
        <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Clock size={12} /> {formatHour(selectedHour)} 기사만 표시 중
          <button
            onClick={() => onSelectHour(null)}
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.75rem', cursor: 'pointer', marginLeft: '0.25rem' }}
          >✕ 전체 보기</button>
        </div>
      )}
    </div>
  );
}

function MiniCalendar({ availableDates, selectedDate, onSelectDate }) {
  const dateSet = useMemo(() => new Set((availableDates || []).map((d) => d.date)), [availableDates]);
  const countMap = useMemo(() => {
    const m = {};
    (availableDates || []).forEach((d) => { m[d.date] = d.count; });
    return m;
  }, [availableDates]);

  const [viewYear, setViewYear] = useState(() => {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    return d.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    return d.getMonth();
  });

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleString('ko-KR', { year: 'numeric', month: 'long' });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="card" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <button className="icon-btn" onClick={prevMonth}><ChevronLeft size={14} /></button>
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#f1f5f9' }}>{monthLabel}</span>
        <button className="icon-btn" onClick={nextMonth}><ChevronRight size={14} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', textAlign: 'center' }}>
        {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
          <div key={d} style={{ fontSize: '0.7rem', color: '#475569', padding: '0.2rem 0' }}>{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const hasData = dateSet.has(dateStr);
          const count = countMap[dateStr] || 0;
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === todayStr;

          return (
            <div
              key={dateStr}
              onClick={() => hasData && onSelectDate(dateStr)}
              title={hasData ? `${count}건` : '수집 데이터 없음'}
              style={{
                padding: '0.3rem 0',
                borderRadius: '5px',
                fontSize: '0.8rem',
                cursor: hasData ? 'pointer' : 'default',
                background: isSelected ? '#0ea5e9' : isToday ? '#1e3a5f' : 'transparent',
                color: isSelected ? '#fff' : hasData ? '#e2e8f0' : '#334155',
                fontWeight: isSelected || isToday ? 700 : 400,
                position: 'relative',
                transition: 'background 0.12s',
              }}
            >
              {day}
              {hasData && !isSelected && (
                <div style={{
                  position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)',
                  width: '4px', height: '4px', borderRadius: '50%',
                  background: count > 50 ? '#4ade80' : count > 10 ? '#fbbf24' : '#38bdf8',
                }} />
              )}
            </div>
          );
        })}
      </div>

      {availableDates && availableDates.length > 0 && (
        <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#475569', display: 'flex', gap: '0.75rem' }}>
          <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#4ade80', marginRight: 3 }} />50건+</span>
          <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#fbbf24', marginRight: 3 }} />10건+</span>
          <span><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#38bdf8', marginRight: 3 }} />1건+</span>
        </div>
      )}
    </div>
  );
}

function ArticleRow({ article }) {
  const [copied, setCopied] = useState(false);
  const collectedTime = article.collected_at
    ? article.collected_at.slice(11, 16)
    : '';
  const score = article.relevance_score;
  const scoreColor = score >= 5 ? '#4ade80' : score >= 3 ? '#fbbf24' : '#38bdf8';

  return (
    <div className="article-row">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '0.7rem', fontWeight: 700, color: '#1e293b',
          background: '#334155', borderRadius: '3px', padding: '1px 6px', color: '#94a3b8',
        }}>{collectedTime}</span>
        <span className="badge badge-blue" style={{ fontSize: '0.65rem' }}>{article.source}</span>
        <span style={{ fontSize: '0.7rem', color: scoreColor, fontWeight: 600 }}>★ {score?.toFixed(1)}</span>
        {article.is_duplicate === 1 && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>중복</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <a
          href={article.link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ flex: 1, fontSize: '0.9rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.3rem', lineHeight: 1.45 }}
        >
          {article.title}
          <ExternalLink size={11} opacity={0.4} style={{ flexShrink: 0 }} />
        </a>
        <button
          className="icon-btn"
          style={{ flexShrink: 0, width: 24, height: 24 }}
          onClick={() => { navigator.clipboard.writeText(article.link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          title="링크 복사"
        >
          {copied ? <CheckCircle size={12} color="#4ade80" /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

export default function ArchivePage() {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [page, setPage] = useState(1);
  const [excludeDuplicates, setExcludeDuplicates] = useState(true);

  const { data: availableDates } = useQuery({
    queryKey: ['available-dates'],
    queryFn: fetchAvailableDates,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['articles-by-date', selectedDate, selectedHour, page, excludeDuplicates],
    queryFn: () => fetchArticlesByDate({
      date: selectedDate,
      page,
      limit: 30,
      excludeDuplicates,
      hourFrom: selectedHour !== null ? selectedHour : 0,
      hourTo: selectedHour !== null ? selectedHour : 23,
    }),
    enabled: !!selectedDate,
    keepPreviousData: true,
  });

  const allArticles = data?.articles || [];
  const articles = selectedSlot
    ? allArticles.filter((a) => {
        const m = parseInt((a.collected_at || '').slice(14, 16), 10);
        return m >= selectedSlot.minuteFrom && m <= selectedSlot.minuteTo;
      })
    : allArticles;
  const total = selectedSlot ? articles.length : (data?.total || 0);
  const pages = selectedSlot ? 1 : (data?.pages || 1);
  const hourlyDist = data?.hourlyDistribution || [];

  const selectedDateLabel = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })
    : '';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <CalendarDays size={20} color="#a78bfa" />
          날짜별 뉴스 조회
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1.25rem', alignItems: 'start' }}>
        <MiniCalendar
          availableDates={availableDates}
          selectedDate={selectedDate}
          onSelectDate={(d) => { setSelectedDate(d); setSelectedHour(null); setSelectedSlot(null); setPage(1); }}
        />

        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9' }}>{selectedDateLabel}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.1rem' }}>
                  총 <strong style={{ color: '#e2e8f0' }}>{total}</strong>건
                  {selectedHour !== null && !selectedSlot && ` · ${formatHour(selectedHour)} 필터`}
                  {selectedSlot && ` · ${selectedSlot.label} 수집분`}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#94a3b8', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={excludeDuplicates}
                  onChange={(e) => { setExcludeDuplicates(e.target.checked); setPage(1); }}
                />
                중복 제외
              </label>
            </div>

            {hourlyDist.length > 0 && (
              <>
                <HourBar
                  hourlyDistribution={hourlyDist}
                  selectedHour={selectedHour}
                  onSelectHour={(h) => { setSelectedHour(h); setSelectedSlot(null); setPage(1); }}
                />
                <SlotPicker
                  hour={selectedHour}
                  articles={allArticles}
                  selectedSlot={selectedSlot}
                  onSelectSlot={(s) => { setSelectedSlot(s); setPage(1); }}
                />
              </>
            )}
          </div>

          <div className="card">
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#475569' }}>
                <div className="spinner" style={{ margin: '0 auto 0.75rem' }} />
                불러오는 중...
              </div>
            ) : articles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#475569' }}>
                <AlertTriangle size={28} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                {selectedDate ? '해당 날짜에 수집된 기사가 없습니다.' : '날짜를 선택하세요.'}
              </div>
            ) : (
              articles.map((a) => <ArticleRow key={a.id} article={a} />)
            )}
          </div>

          {pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={14} />
              </button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>{page} / {pages}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .article-row {
          padding: 0.75rem 0;
          border-bottom: 1px solid #1e293b;
        }
        .article-row:last-child { border-bottom: none; }
        .icon-btn {
          width: 28px; height: 28px; border-radius: 5px; border: none;
          background: #334155; color: #94a3b8;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
        }
        .icon-btn:hover { background: #475569; color: #e2e8f0; }
        .spinner {
          width: 20px; height: 20px;
          border: 2px solid #334155; border-top-color: #38bdf8;
          border-radius: 50%; animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 640px) {
          .archive-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
